import {
  Badge,
  Button,
  cn,
  FilePreviewDialog,
  fileExtensionFromMimeType,
  getFileIcon,
  getFilePreviewKind,
  Icon,
  type PartialFileUpload,
  parseFilename,
  useFilePreviewDialog,
} from "@qw/ui";
import type { FileUIPart, TextUIPart } from "ai";
import { type FC, useMemo } from "react";

import type { ExtendedUIMessage } from "../chat.types";
import { CHAT_FILE_SUPPORTED_MIME_TYPES } from "./chat.constants";
import { ChatMessage, ChatMessageContent } from "./chat-message";

const isSupportedChatMessageFile = (mediaType: string | undefined) =>
  Boolean(mediaType && (CHAT_FILE_SUPPORTED_MIME_TYPES.has(mediaType) || getFilePreviewKind(mediaType)));

const getApiFileId = (url: string | undefined) => {
  const apiFilesPrefix = "/api/files/";

  if (!url?.startsWith(apiFilesPrefix)) {
    return;
  }

  return url.slice(apiFilesPrefix.length);
};

const toApiFileUpload = (part: FileUIPart): PartialFileUpload | undefined => {
  const id = getApiFileId(part.url);

  if (!id || !part.filename || !isSupportedChatMessageFile(part.mediaType)) {
    return;
  }

  return {
    id,
    filename: part.filename,
    mimeType: part.mediaType,
  };
};

export const UserChatSessionMessage = ({ message }: { message: ExtendedUIMessage }) => {
  const files = useMemo(() => getMessageApiFiles(message.parts), [message.parts]);

  const textParts = useMemo(
    () => message.parts?.filter((part): part is TextUIPart => part.type === "text" && !!part.text?.trim()) || [],
    [message.parts]
  );

  const hasFileContent = files.length > 0;
  const hasTextContent = textParts.length > 0;
  const hasContent = hasFileContent || hasTextContent;

  if (!hasContent) {
    return null;
  }

  return (
    <ChatMessage key={message.id}>
      <div className={cn("flex-auto -mx-2 p-1.5 flex flex-col gap-1 border border-border bg-muted rounded-md")}>
        {hasFileContent && <UserChatMessageFileParts files={files} />}
        {hasTextContent && (
          <ChatMessageContent>
            <UserChatMessageTextParts parts={textParts} />
          </ChatMessageContent>
        )}
      </div>
    </ChatMessage>
  );
};

const getMessageApiFiles = (parts: ExtendedUIMessage["parts"]): PartialFileUpload[] => {
  const files: PartialFileUpload[] = [];

  for (const part of parts ?? []) {
    if (part.type !== "file") {
      continue;
    }

    const file = toApiFileUpload(part);

    if (file) {
      files.push(file);
    }
  }

  return files;
};

interface UserChatMessageTextPartsProps {
  parts: TextUIPart[];
}

const UserChatMessageTextParts: FC<UserChatMessageTextPartsProps> = ({ parts }) => {
  return (
    <div className="-mx-1.5 px-2 pt-0.5 pb-0.5">
      {parts.map((part, index) => {
        if (!part.text.trim()) {
          return null;
        }
        return (
          <p key={index} className="my-0.5">
            {part.text}
          </p>
        );
      })}
    </div>
  );
};

interface UserChatMessageFilePartsProps {
  files: PartialFileUpload[];
}

const UserChatMessageFileParts: FC<UserChatMessageFilePartsProps> = ({ files }) => {
  const filePreview = useFilePreviewDialog({ files });

  if (!files.length) {
    return null;
  }

  return (
    <>
      <ul className="w-full min-w-0 flex flex-wrap gap-1">
        {files.map((file) => (
          <UserChatMessageFile key={file.id} file={file} openFile={filePreview.openFile} />
        ))}
      </ul>
      <FilePreviewDialog {...filePreview.dialogProps} />
    </>
  );
};

interface UserChatMessageFileProps {
  file: PartialFileUpload;
  openFile: (file: PartialFileUpload) => void;
}

const UserChatMessageFile: FC<UserChatMessageFileProps> = ({ file, openFile }) => {
  const icon = useMemo(() => getFileIcon(file.mimeType), [file.mimeType]);

  return (
    <li className="max-w-full leading-none">
      <Button
        size="xs"
        variant="outline"
        type="button"
        onClick={() => openFile(file)}
        className={cn("relative max-w-full pl-1.25 pr-1.25 py-1 gap-1.5")}
      >
        <span className="size-3 flex-0">
          <Icon icon={icon} className="text-sm text-foreground opacity-50 filled" />
        </span>
        <span className="text-xs text-foreground truncate flex-1">{parseFilename(file.filename).name}</span>
        <Badge variant="secondary" size="2xs" type="square" className="uppercase flex-none -mr-0.5">
          {fileExtensionFromMimeType(file.mimeType)}
        </Badge>
      </Button>
    </li>
  );
};
